import { useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { useCancellationFeedback, REASON_LABELS, formatFeedbackDate, CancellationFeedbackItem } from "../hooks/useCancellationFeedback";
import { EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";

const CancellationFeedbackPage: React.FC = () => {
  const { feedbackList, loading } = useCancellationFeedback();
  const [selectedFeedback, setSelectedFeedback] = useState<CancellationFeedbackItem | null>(null);

  return (
    <DashboardWrapper>
      <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6 md:h-[calc(100vh-64px)] overflow-hidden">
        <h1 className="text-xl md:text-2xl font-heading text-gray-400">Cancellation Feedback</h1>

        {/* Stats Row */}
        <div className="w-full grid grid-cols-3 gap-2 shrink-0">
          <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
            <div className="text-xl md:text-2xl text-gray-400 font-bold">
              {feedbackList.length}
            </div>
            <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Cancellations</div>
          </div>
          <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
            <div className="text-xl md:text-2xl text-gray-400 font-bold">
              {feedbackList.filter(f => f.cancelType === "immediate").length}
            </div>
            <div className="text-[10px] md:text-xs text-gray-500 uppercase">Immediate</div>
          </div>
          <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
            <div className="text-xl md:text-2xl text-gray-400 font-bold">
              {feedbackList.filter(f => f.cancelType === "periodEnd").length}
            </div>
            <div className="text-[10px] md:text-xs text-gray-500 uppercase">End of Period</div>
          </div>
        </div>

        {/* Feedback Table */}
        <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1.5fr_1fr_1fr_2fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
            <div className="hidden md:block">Date</div>
            <div className="hidden md:block">Plan</div>
            <div className="hidden md:block">Type</div>
            <div>Primary Reason</div>
            <div className="hidden md:block">Actions</div>
          </div>

          {/* Table Body */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-gray-400">
                Loading feedback...
              </div>
            ) : feedbackList.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400">
                No cancellation feedback found
              </div>
            ) : (
              feedbackList.map((item) => (
                <div
                  key={item.id + item.uid}
                  className="grid grid-cols-[1fr_auto] md:grid-cols-[1.5fr_1fr_1fr_2fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors cursor-pointer md:cursor-default"
                  onClick={() => {
                    if (window.innerWidth < 768) {
                      setSelectedFeedback(item);
                    }
                  }}
                >
                  {/* Date - Desktop only */}
                  <div className="hidden md:block">
                    <span className="text-sm text-gray-600">
                      {formatFeedbackDate(item.submittedAt)}
                    </span>
                  </div>

                  {/* Plan - Desktop only */}
                  <div className="hidden md:block">
                    <span className="inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full bg-gray-100 text-gray-800">
                      {item.subscriptionType}
                    </span>
                  </div>

                  {/* Cancel Type - Desktop only */}
                  <div className="hidden md:block">
                    <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${
                      item.cancelType === "immediate" 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {item.cancelType === "immediate" ? "Immediate" : "Period End"}
                    </span>
                  </div>

                  {/* Primary Reason (Q1) */}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-xs text-gray-400 md:hidden">
                      {formatFeedbackDate(item.submittedAt)} • {item.subscriptionType}
                    </span>
                    <p className="text-sm text-gray-600 line-clamp-1">
                      {item.q1 ? REASON_LABELS[item.q1]?.title || item.q1 : "Not specified"}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFeedback(item);
                      }}
                      className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                      title="View details"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* View Details Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h2 className="text-lg font-heading">Cancellation Details</h2>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="px-4 sm:px-6 py-4 space-y-5">
                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Date</label>
                    <p className="text-sm">{formatFeedbackDate(selectedFeedback.submittedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Cancel Type</label>
                    <span className={`inline-flex px-2.5 py-1 text-xs uppercase rounded-full font-medium ${
                      selectedFeedback.cancelType === "immediate" 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {selectedFeedback.cancelType === "immediate" ? "Canceled Immediately" : "Canceled at Period End"}
                    </span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Plan</label>
                    <p className="text-sm capitalize">{selectedFeedback.subscriptionType}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">User ID</label>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded block overflow-x-auto truncate">
                      {selectedFeedback.uid}
                    </code>
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Question 1 */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Q1: Main Reason for Cancelling</label>
                  <div className="bg-gray-50 p-3 rounded-lg border">
                    {selectedFeedback.q1 ? (
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {REASON_LABELS[selectedFeedback.q1]?.title || selectedFeedback.q1}
                        </p>
                        {REASON_LABELS[selectedFeedback.q1]?.subtitle && (
                          <p className="text-xs text-gray-500 mt-1">
                            {REASON_LABELS[selectedFeedback.q1].subtitle}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Not specified</p>
                    )}
                  </div>
                </div>

                {/* Question 2 */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Q2: What would have made you stay?</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedFeedback.q2.length > 0 ? (
                      selectedFeedback.q2.map((reasonId) => (
                        <span 
                          key={reasonId} 
                          className="inline-flex items-center px-3 py-1.5 text-xs rounded-full bg-blue-50 text-blue-800 border border-blue-100"
                        >
                          {REASON_LABELS[reasonId]?.title || reasonId}
                        </span>
                      ))
                    ) : (
                      <p className="text-sm text-gray-400 italic">None selected</p>
                    )}
                  </div>
                </div>

                {/* Question 3 */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Q3: Anything else? (Optional)</label>
                  <div className="bg-gray-50 p-3 rounded-lg border min-h-[60px]">
                    {selectedFeedback.q3 ? (
                      <p className="text-sm text-gray-700 whitespace-pre-line">{selectedFeedback.q3}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No additional comments</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t bg-white sticky bottom-0">
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default CancellationFeedbackPage;